"use client"

import type React from "react"
import { useEffect, useRef, useState } from "react"

interface ScrollRevealProps {
  children: React.ReactNode
  delay?: number
  duration?: number
}

export function ScrollReveal({ children, delay = 0, duration = 0.8 }: ScrollRevealProps) {
  const elementRef = useRef(null)
  const [isClient, setIsClient] = useState(false)

  useEffect(() => {
    setIsClient(true)
  }, [])

  useEffect(() => {
    if (!isClient || typeof window === "undefined") return

    // Wait for GSAP to be available
    const checkGsap = () => {
      const gsap = (window as any).gsap
      const ScrollTrigger = (window as any).ScrollTrigger

      if (!gsap || !ScrollTrigger) {
        // Retry in 100ms if GSAP not loaded yet
        setTimeout(checkGsap, 100)
        return
      }

      requestAnimationFrame(() => {
        if (!elementRef.current) return

        gsap.registerPlugin(ScrollTrigger)

        gsap.fromTo(
          elementRef.current,
          { opacity: 0, y: 40 },
          {
            opacity: 1,
            y: 0,
            duration,
            delay,
            ease: "power3.out",
            scrollTrigger: {
              trigger: elementRef.current,
              start: "top 80%",
              end: "top 50%",
              toggleActions: "play none none reverse",
            },
          },
        )
      })
    }

    checkGsap()

    return () => {
      if ((window as any).ScrollTrigger) {
        ;(window as any).ScrollTrigger.getAll().forEach((trigger: any) => {
          try {
            trigger.kill()
          } catch (e) {
            // Ignore cleanup errors
          }
        })
      }
    }
  }, [delay, duration, isClient])

  return <div ref={elementRef}>{children}</div>
}
